import {type Agent, type AgentOptions, buildAgent} from '@atproto/lex'

/**
 * The one public identity presented by ErisSky.
 *
 * Eris's live ATProto identity supplies the profile data while this facade
 * applies the same presentation consistently to every account in the app.
 */
export const ERIS_IDENTITY = {
  handle: 'isolyth.dev',
  displayName: 'Eris',
  avatar:
    'https://cdn.bsky.app/img/avatar/plain/did:plc:allu5vs3gnm2wm7jzf4rad3r/bafkreicd25pgrehilwmi5ntoslyzzqqs3onxw35odihr6ypast24ent4he',
  sourceDid: 'did:plc:allu5vs3gnm2wm7jzf4rad3r',
  sourceHandle: 'isolyth.dev',
} as const

const ORIGINAL_IDENTITY = '__erisOriginalIdentity'

type OriginalIdentity = {
  handle: string
  displayName?: string
  avatar?: string
}

type IdentityLike = {
  did?: unknown
  handle?: unknown
  displayName?: unknown
  avatar?: unknown
  [ORIGINAL_IDENTITY]?: OriginalIdentity
}

function isProfileView(value: object): value is IdentityLike & {
  did: string
  handle: string
} {
  const candidate = value as IdentityLike
  return (
    typeof candidate.did === 'string' &&
    candidate.did.startsWith('did:') &&
    typeof candidate.handle === 'string'
  )
}

/**
 * Rewrites every profile-shaped object in a JSON response in place.
 *
 * The original identity is retained as JSON-safe metadata. Non-rendering code
 * can therefore keep using the real handle for record URLs, search matching,
 * and writes while every ordinary profile consumer sees Eris.
 */
export function overlayErisIdentity<T>(value: T): T {
  if (!value || typeof value !== 'object') return value

  if (Array.isArray(value)) {
    for (const item of value) overlayErisIdentity(item)
    return value
  }

  const object = value as Record<string, unknown>
  for (const child of Object.values(object)) overlayErisIdentity(child)

  if (isProfileView(object)) {
    object[ORIGINAL_IDENTITY] ??= {
      handle: object.handle,
      displayName:
        typeof object.displayName === 'string' ? object.displayName : undefined,
      avatar: typeof object.avatar === 'string' ? object.avatar : undefined,
    }
    object.handle = ERIS_IDENTITY.handle
    object.displayName = ERIS_IDENTITY.displayName
    object.avatar = ERIS_IDENTITY.avatar
  }

  return value
}

export function getOriginalProfileIdentity(
  profile: IdentityLike,
): OriginalIdentity {
  return (
    profile[ORIGINAL_IDENTITY] ?? {
      handle:
        typeof profile.handle === 'string'
          ? profile.handle
          : ERIS_IDENTITY.sourceHandle,
      displayName:
        typeof profile.displayName === 'string'
          ? profile.displayName
          : undefined,
      avatar: typeof profile.avatar === 'string' ? profile.avatar : undefined,
    }
  )
}

export function getOriginalProfileHandle(profile: IdentityLike): string {
  return getOriginalProfileIdentity(profile).handle
}

export function resolveErisProfileIdentifier(identifier: string): string {
  return identifier.toLocaleLowerCase() === ERIS_IDENTITY.handle
    ? ERIS_IDENTITY.sourceDid
    : identifier
}

const PROFILE_PATH =
  /^(\/profile\/)[^/?#]+(?=\/?(?:[?#]|$)|\/(?:followers|follows|known-followers|search|rss)(?:[/?#]|$))/i

/** Canonicalize profile-page URLs without disturbing post/feed/list URLs. */
export function canonicalizeErisProfilePath(path: string): string {
  return path.replace(PROFILE_PATH, `$1${ERIS_IDENTITY.handle}`)
}

async function overlayErisResponse(response: Response): Promise<Response> {
  if (
    !response.ok ||
    !response.headers.get('content-type')?.includes('application/json')
  ) {
    return response
  }

  const headers = new Headers(response.headers)
  headers.delete('content-encoding')
  headers.delete('content-length')
  const init = {
    status: response.status,
    statusText: response.statusText,
    headers,
  }
  const text = await response.text()
  if (!text) return new Response(null, init)

  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    return new Response(text, init)
  }

  return new Response(JSON.stringify(overlayErisIdentity(body)), {
    ...init,
  })
}

/** Wrap an appview/chat agent so all profile views cross one identity boundary. */
export function withErisIdentity(agent: Agent | AgentOptions): Agent {
  const source = buildAgent(agent)
  return {
    get did() {
      return source.did
    },
    async fetchHandler(path, init) {
      return overlayErisResponse(await source.fetchHandler(path, init))
    },
  }
}
